/**
 * The Mockup Maker's colour layer, ported — MTC-FCS-041.
 * ============================================================================
 * A straight port of `source--mockup-maker/src/lib/color.ts`, types stripped.
 * Kept faithful rather than rewritten, for one reason: these templates get
 * their look from ~40 CSS variables that this file computes, and the parts
 * that look arbitrary are the parts doing the work — lifting a brand colour
 * until it is legible as TEXT is different from lifting it until it is legible
 * as a BACKGROUND, and the designs rely on both being right.
 *
 * If the maker's version changes, re-port it. Do not "improve" it here: the
 * two have to agree or the same palette renders differently in each tool.
 */

const HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i

/** Accepts `abc`, `#abc`, `aabbcc`, `#aabbcc`. Returns `#aabbcc` or null. */
export function normalizeHex(input) {
  const value = (input || "").trim()
  if (!HEX_RE.test(value)) return null
  let hex = value.replace("#", "").toLowerCase()
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map(c => c + c)
      .join("")
  }
  return `#${hex}`
}

/** Same, but never fails — falls back to the supplied default. */
export function safeHex(input, fallback) {
  return normalizeHex(input) ?? fallback
}

export function hexToRgb(hex) {
  const normalized = normalizeHex(hex) ?? "#000000"
  return {
    r: parseInt(normalized.slice(1, 3), 16),
    g: parseInt(normalized.slice(3, 5), 16),
    b: parseInt(normalized.slice(5, 7), 16),
  }
}

const toHexPair = n =>
  Math.max(0, Math.min(255, Math.round(n)))
    .toString(16)
    .padStart(2, "0")

export function rgbToHex({ r, g, b }) {
  return `#${toHexPair(r)}${toHexPair(g)}${toHexPair(b)}`
}

/** `mix('#ff0000', '#ffffff', 0.25)` → 25% of the way to white. */
export function mix(from, to, amount) {
  const a = hexToRgb(from)
  const b = hexToRgb(to)
  const t = Math.max(0, Math.min(1, amount))
  return rgbToHex({
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
  })
}

export const lighten = (hex, amount) => mix(hex, "#ffffff", amount)
export const darken = (hex, amount) => mix(hex, "#000000", amount)

/** `rgba()` string from a hex + alpha. */
export function alpha(hex, a) {
  const { r, g, b } = hexToRgb(hex)
  return `rgba(${r}, ${g}, ${b}, ${Math.max(0, Math.min(1, a))})`
}

/** Rough colourfulness, 0 (grey) → 1 (saturated). */
export function chroma(hex) {
  const { r, g, b } = hexToRgb(hex)
  return (Math.max(r, g, b) - Math.min(r, g, b)) / 255
}

/** WCAG relative luminance. */
export function luminance(hex) {
  const { r, g, b } = hexToRgb(hex)
  const channel = v => {
    const s = v / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

/** WCAG contrast ratio between two colours (1 → 21). */
export function contrast(a, b) {
  const la = luminance(a)
  const lb = luminance(b)
  const [hi, lo] = la > lb ? [la, lb] : [lb, la]
  return (hi + 0.05) / (lo + 0.05)
}

/**
 * Near-white or near-black text, whichever stays readable on `bg`.
 *
 * Picking the BETTER of two is not the same as picking one that PASSES: a
 * mid-tone brand gives a dark ink at 4.35:1, just under AA, on every button
 * and monogram. When neither candidate clears 4.5 the winner is pushed the
 * rest of the way to pure black or white — the only direction left that does
 * not touch the background the design chose.
 */
export function readableOn(bg, light = "#ffffff", dark = "#0b1220") {
  const best = contrast(bg, light) >= contrast(bg, dark) ? light : dark
  if (contrast(bg, best) >= 4.5) return best
  const extreme = best === light ? "#ffffff" : "#000000"
  return contrast(bg, extreme) > contrast(bg, best) ? extreme : best
}

/**
 * Nudges a brand colour until it is legible against the surface it sits on.
 * Hue is preserved — the mix only goes toward white or black.
 */
export function toneForSurface(brand, surface, minRatio = 3.2) {
  const goingLighter = luminance(surface) < 0.4
  let out = brand
  for (let i = 0; i < 14 && contrast(out, surface) < minRatio; i++) {
    out = goingLighter ? lighten(out, 0.08) : darken(out, 0.08)
  }
  return out
}

/**
 * Every token the template stylesheets read. All five templates read the same
 * names, so a template only has to think about layout, never about palette.
 */
export function buildTheme(primaryInput, secondaryInput, mode = "light", template = "v3") {
  const primary = safeHex(primaryInput, "#1d4ed8")
  const secondary = safeHex(secondaryInput, "#f97316")
  const isDark = mode === "dark"

  // v2 ships as a near-black canvas; the rest ship light. In dark mode each
  // gets the surfaces its own design language expects.
  const darkSurfaces =
    template === "v2"
      ? { page: "#0b0f14", surface: "#151a21", surface2: "#10151b", surface3: "#1b222b" }
      : { page: "#0b1120", surface: "#131c2e", surface2: "#18233a", surface3: "#1f2c47" }

  const page = isDark ? darkSurfaces.page : "#ffffff"
  const surface = isDark ? darkSurfaces.surface : "#ffffff"
  const surface2 = isDark ? darkSurfaces.surface2 : "#f8f9fa"
  const surface3 = isDark ? darkSurfaces.surface3 : "#eef2f7"

  const brand = toneForSurface(primary, isDark ? surface : "#ffffff", isDark ? 3.6 : 2.6)
  const accent = toneForSurface(secondary, isDark ? surface : "#ffffff", isDark ? 3.6 : 2.6)

  const ink = isDark ? "#f5f8fc" : "#0d1526"
  const ink2 = isDark ? "#c3cddd" : "#3b4a63"
  const ink3 = isDark ? "#8b99b1" : "#55637a"

  // The footer / dark band. Light mode: an ink-tinted brand black.
  const panel = isDark ? "#080e1a" : darken(mix(primary, "#111827", 0.82), 0.1)

  // `--brand` and `--accent` are tuned as BACKGROUNDS with ink on top. Used
  // directly as text they routinely fail WCAG, so these are the same hues
  // lifted until they read at small sizes.
  const brandOnSurface = toneForSurface(primary, surface, 4.5)
  const accentOnSurface = toneForSurface(secondary, surface, 4.5)
  const brandOnPanel = toneForSurface(primary, panel, 4.5)
  const accentOnPanel = toneForSurface(secondary, panel, 4.5)

  const overlayInk = mix(primary, "#0a0f18", 0.88)
  const liftedBrand = toneForSurface(primary, overlayInk, 4.6)
  const onImageAccent = toneForSurface(secondary, overlayInk, 4.6)

  // Lifting a near-grey primary far enough to read on a photo drains what
  // colour it had, so in those pairings the secondary takes the highlight.
  const onImageBrand =
    chroma(liftedBrand) < 0.12 && chroma(onImageAccent) > chroma(liftedBrand) + 0.1
      ? onImageAccent
      : liftedBrand

  return {
    "--brand": brand,
    "--brand-strong": isDark ? lighten(brand, 0.12) : darken(brand, 0.14),
    "--brand-soft": isDark ? alpha(brand, 0.16) : alpha(brand, 0.1),
    "--brand-tint": isDark ? alpha(brand, 0.28) : mix(brand, "#ffffff", 0.9),
    "--brand-ink": readableOn(brand),
    "--brand-on-surface": brandOnSurface,
    "--brand-on-panel": brandOnPanel,
    "--accent": accent,
    "--accent-strong": isDark ? lighten(accent, 0.12) : darken(accent, 0.14),
    "--accent-soft": isDark ? alpha(accent, 0.16) : alpha(accent, 0.1),
    "--accent-ink": readableOn(accent),
    "--accent-on-surface": accentOnSurface,
    "--accent-on-panel": accentOnPanel,

    "--page": page,
    "--surface": surface,
    "--surface-2": surface2,
    "--surface-3": surface3,
    "--panel": panel,
    "--panel-ink": "#ffffff",
    "--panel-muted": isDark ? "rgba(255,255,255,0.58)" : "rgba(255,255,255,0.66)",

    "--ink": ink,
    "--ink-2": ink2,
    "--ink-3": ink3,

    "--on-image": "#ffffff",
    "--on-image-dim": "rgba(255,255,255,0.80)",
    "--on-image-brand": onImageBrand,
    "--on-image-accent": onImageAccent,

    "--line": isDark ? (template === "v2" ? "#2a2f36" : "rgba(255,255,255,0.10)") : "#e9ecef",
    "--line-strong": isDark
      ? template === "v2"
        ? "#363c45"
        : "rgba(255,255,255,0.18)"
      : "#d3dae4",
    "--ring": isDark ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.9)",

    // Two-stop shadows: a tight contact shadow plus a wide soft one. One large
    // blur reads as a cheap drop shadow; the pair reads as depth.
    "--shadow-sm": isDark
      ? "0 1px 2px rgba(0,0,0,0.45)"
      : "0 1px 2px rgba(16,24,40,0.04), 0 1px 3px rgba(16,24,40,0.06)",
    "--shadow-md": isDark
      ? "0 2px 6px rgba(0,0,0,0.40), 0 14px 32px rgba(0,0,0,0.45)"
      : "0 2px 4px rgba(16,24,40,0.04), 0 12px 28px rgba(16,24,40,0.08)",
    "--shadow-lg": isDark
      ? "0 6px 14px rgba(0,0,0,0.45), 0 34px 80px rgba(0,0,0,0.60)"
      : "0 4px 8px rgba(16,24,40,0.04), 0 28px 64px rgba(16,24,40,0.16)",

    "--overlay-ink": overlayInk,
    "--hero-overlay": isDark
      ? `linear-gradient(96deg, ${alpha(overlayInk, 0.95)} 0%, ${alpha(overlayInk, 0.88)} 46%, ${alpha(overlayInk, 0.6)} 100%)`
      : `linear-gradient(96deg, ${alpha(overlayInk, 0.92)} 0%, ${alpha(overlayInk, 0.8)} 46%, ${alpha(overlayInk, 0.46)} 100%)`,
    "--hero-vignette": `linear-gradient(180deg, ${alpha(overlayInk, 0.55)} 0%, rgba(0,0,0,0) 22%, rgba(0,0,0,0) 68%, ${alpha(overlayInk, 0.5)} 100%)`,
    "--glass": isDark ? "rgba(255,255,255,0.07)" : "rgba(255,255,255,0.12)",
    "--glass-line": "rgba(255,255,255,0.18)",

    "--star": "#fbbf24",
  }
}

/** The token object, as an inline style React can apply. */
export function tokensToStyle(tokens) {
  return tokens
}
